import { supabase } from "@/lib/supabase";
import type { Job, JobChecklist, JobChecklistItem, JobChecklistPhaseKey, ProjectType } from "@/data/types";

export const JOB_CHECKLIST_PHASES: JobChecklistPhaseKey[] = ["prep", "demo", "rough", "install", "finish", "qa", "rework", "custom"];
export const JOB_CHECKLIST_AUTHORING_PHASES: JobChecklistPhaseKey[] = ["prep", "demo", "rough", "install", "finish", "qa", "custom"];

export const DEFAULT_JOB_CHECKLIST_PHASE_LABELS: Record<JobChecklistPhaseKey, string> = {
  prep: "Prep",
  demo: "Demo",
  rough: "Rough",
  install: "Install",
  finish: "Finish",
  qa: "QA",
  custom: "Custom",
  rework: "Rework",
};

type ChecklistTemplateKey =
  | "bath"
  | "flooring"
  | "paint"
  | "windows_doors"
  | "roofing"
  | "plumbing"
  | "drywall"
  | "general";

type ChecklistTemplateScope = Pick<Job, "organizationId" | "projectType" | "scopeSummary" | "notes" | "projectConfig">;

type ChecklistTemplateRow = {
  id?: string | null;
  organization_id?: string | null;
  key?: string | null;
  name?: string | null;
  trade_key?: string | null;
  is_default?: boolean | null;
};

type ChecklistTemplateItemRow = {
  id?: string | null;
  phase_key?: string | null;
  item_index?: number | null;
  label?: string | null;
  proof_label?: string | null;
  requires_proof?: boolean | null;
  sort_order?: number | null;
};

type ChecklistTemplatePhaseRow = {
  phase_key?: string | null;
  label?: string | null;
  sort_order?: number | null;
};

type ExistingChecklistRow = {
  id?: string | null;
  phase_key?: string | null;
  phase_label?: string | null;
  item_index?: number | null;
  label?: string | null;
  proof_label?: string | null;
  requires_proof?: boolean | null;
  sort_order?: number | null;
  is_custom?: boolean | null;
  is_active?: boolean | null;
  template_id?: string | null;
  template_item_id?: string | null;
};

function normalizeText(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

function resolvePhaseLabel(phaseKey: JobChecklistPhaseKey, phaseLabels?: Partial<Record<JobChecklistPhaseKey, string>>): string {
  return String(phaseLabels?.[phaseKey] ?? "").trim() || DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phaseKey];
}

function inferChecklistTemplateKeyFromProjectType(value?: ProjectType | string): ChecklistTemplateKey | null {
  const text = normalizeText(value);
  if (!text || text === "other" || text === "general") return null;
  if (/floor|lvp|vinyl|laminate|hardwood|carpet/.test(text)) return "flooring";
  if (/paint/.test(text)) return "paint";
  if (/window|door/.test(text)) return "windows_doors";
  if (/roof|shingle|reroof|gutter/.test(text)) return "roofing";
  if (/plumb|pipe|valve|toilet|faucet|drain/.test(text)) return "plumbing";
  if (/drywall|sheetrock/.test(text)) return "drywall";
  if (/bath|shower|tub|wet space|wet-space/.test(text)) return "bath";
  return null;
}

export function inferChecklistTemplateKey(input: {
  projectType?: ProjectType | string;
  scopeSummary?: string;
  notes?: string;
  projectConfig?: unknown;
}): ChecklistTemplateKey {
  const projectTypeKey = inferChecklistTemplateKeyFromProjectType(input.projectType);
  if (projectTypeKey) return projectTypeKey;

  const config = input.projectConfig && typeof input.projectConfig === "object"
    ? input.projectConfig as Record<string, unknown>
    : {};
  const text = [
    input.projectType,
    input.scopeSummary,
    input.notes,
    config.autoProjectType,
    config.projectCategory,
    config.consultationCategory,
    config.workDescription,
    Array.isArray(config.selectedVerticals) ? config.selectedVerticals.join(" ") : "",
  ].map(normalizeText).join(" ");

  if (/floor|lvp|vinyl|laminate|tile floor|hardwood|carpet/.test(text)) return "flooring";
  if (/paint|painting|wall color|ceiling color/.test(text)) return "paint";
  if (/window|door|entry door|patio door/.test(text)) return "windows_doors";
  if (/roof|shingle|reroof|gutter/.test(text)) return "roofing";
  if (/plumb|pipe|valve|toilet|faucet|drain/.test(text)) return "plumbing";
  if (/drywall|sheetrock|texture|patch wall/.test(text)) return "drywall";
  if (/bath|shower|tub|wet space|wet-space/.test(text)) return "bath";
  return "general";
}

function buildEmptyChecklist(key: ChecklistTemplateKey): JobChecklist {
  return {
    templateKey: key,
    vertical: key,
    items: [],
    updatedAt: new Date().toISOString(),
  };
}

function buildChecklistFromDbRows(
  template: ChecklistTemplateRow,
  rows: ChecklistTemplateItemRow[],
  phases: ChecklistTemplatePhaseRow[],
): JobChecklist | null {
  const templateKey = String(template.key ?? template.trade_key ?? "").trim();
  const label = String(template.name ?? "").trim();
  if (!templateKey || !label || rows.length === 0) return null;

  const phaseLabels = phases.reduce<Partial<Record<JobChecklistPhaseKey, string>>>((acc, row) => {
    const phase = String(row.phase_key ?? "");
    if (JOB_CHECKLIST_PHASES.includes(phase as JobChecklistPhaseKey)) {
      acc[phase as JobChecklistPhaseKey] = String(row.label ?? "").trim();
    }
    return acc;
  }, {});

  const items = normalizeChecklistItems(rows.map((row) => {
    const phase = String(row.phase_key ?? "");
    const phaseKey = JOB_CHECKLIST_PHASES.includes(phase as JobChecklistPhaseKey)
      ? phase as JobChecklistPhaseKey
      : "install";
    return {
      phaseKey,
      phaseLabel: resolvePhaseLabel(phaseKey, phaseLabels),
      itemIndex: Number.isFinite(Number(row.item_index)) ? Number(row.item_index) : undefined,
      label: String(row.label ?? "").trim(),
      proofLabel: String(row.proof_label ?? "").trim() || null,
      requiresProof: Boolean(row.requires_proof),
      sortOrder: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : undefined,
      isActive: true,
      templateId: String(template.id ?? "").trim() || null,
      templateItemId: String(row.id ?? "").trim() || null,
    };
  }));

  if (items.length === 0) return null;
  return {
    templateKey,
    vertical: label,
    items,
    updatedAt: new Date().toISOString(),
  };
}

async function loadChecklistTemplateFromDb(
  key: ChecklistTemplateKey,
  organizationId?: string,
): Promise<JobChecklist | null> {
  const orgId = String(organizationId ?? "").trim();
  let query = supabase
    .from("job_checklist_templates")
    .select("id, organization_id, key, name, trade_key, is_default")
    .eq("is_active", true);

  query = query.or(`key.eq.${key},trade_key.eq.${key}`);

  const { data: templates, error: templateError } = await query;
  if (templateError) {
    console.warn("[job-checklist] Could not load checklist template from DB:", templateError);
    return null;
  }

  const selected = ((templates ?? []) as ChecklistTemplateRow[])
    .filter((template) => {
      const templateOrgId = String(template.organization_id ?? "").trim();
      return orgId ? !templateOrgId || templateOrgId === orgId : !templateOrgId;
    })
    .sort((a, b) => {
      const aOrg = String(a.organization_id ?? "").trim();
      const bOrg = String(b.organization_id ?? "").trim();
      if (aOrg === orgId && bOrg !== orgId) return -1;
      if (bOrg === orgId && aOrg !== orgId) return 1;
      if (a.is_default && !b.is_default) return -1;
      if (b.is_default && !a.is_default) return 1;
      return 0;
    })[0];

  const templateId = String(selected?.id ?? "").trim();
  if (!selected || !templateId) return null;

  const { data: items, error: itemsError } = await supabase
    .from("job_checklist_template_items")
    .select("id, phase_key, item_index, label, proof_label, requires_proof, sort_order")
    .eq("template_id", templateId)
    .order("sort_order", { ascending: true })
    .order("phase_key", { ascending: true })
    .order("item_index", { ascending: true });

  if (itemsError) {
    console.warn("[job-checklist] Could not load checklist template items from DB:", itemsError);
    return null;
  }

  const { data: phases, error: phasesError } = await supabase
    .from("job_checklist_template_phases")
    .select("phase_key, label, sort_order")
    .eq("template_id", templateId)
    .order("sort_order", { ascending: true })
    .order("phase_key", { ascending: true });

  if (phasesError) {
    console.warn("[job-checklist] Could not load checklist template phases from DB:", phasesError);
  }

  return buildChecklistFromDbRows(
    selected,
    (items ?? []) as ChecklistTemplateItemRow[],
    phasesError ? [] : (phases ?? []) as ChecklistTemplatePhaseRow[],
  );
}

async function loadChecklistTemplateById(templateId: string): Promise<JobChecklist | null> {
  const id = String(templateId ?? "").trim();
  if (!id) return null;

  const { data: template, error: templateError } = await supabase
    .from("job_checklist_templates")
    .select("id, organization_id, key, name, trade_key, is_default")
    .eq("id", id)
    .eq("is_active", true)
    .maybeSingle();

  if (templateError || !template) {
    if (templateError) console.warn("[job-checklist] Could not load linked checklist template:", templateError);
    return null;
  }

  const { data: items, error: itemsError } = await supabase
    .from("job_checklist_template_items")
    .select("id, phase_key, item_index, label, proof_label, requires_proof, sort_order")
    .eq("template_id", id)
    .order("phase_key", { ascending: true })
    .order("sort_order", { ascending: true })
    .order("item_index", { ascending: true });

  if (itemsError) {
    console.warn("[job-checklist] Could not load linked checklist template items:", itemsError);
    return null;
  }

  const { data: phases, error: phasesError } = await supabase
    .from("job_checklist_template_phases")
    .select("phase_key, label, sort_order")
    .eq("template_id", id)
    .order("sort_order", { ascending: true })
    .order("phase_key", { ascending: true });

  if (phasesError) {
    console.warn("[job-checklist] Could not load linked checklist template phases:", phasesError);
  }

  return buildChecklistFromDbRows(
    template as ChecklistTemplateRow,
    (items ?? []) as ChecklistTemplateItemRow[],
    phasesError ? [] : (phases ?? []) as ChecklistTemplatePhaseRow[],
  );
}

export function buildDefaultJobChecklist(job: Pick<Job, "projectType" | "scopeSummary" | "notes" | "projectConfig">): JobChecklist {
  return buildEmptyChecklist(inferChecklistTemplateKey(job));
}

export async function loadDefaultJobChecklist(job: ChecklistTemplateScope): Promise<JobChecklist> {
  const templateKey = inferChecklistTemplateKey(job);
  const specific = await loadChecklistTemplateFromDb(templateKey, job.organizationId);
  if (specific) return specific;
  // Fall back to the global "general" template if the trade-specific one isn't seeded yet.
  if (templateKey !== "general") {
    const general = await loadChecklistTemplateFromDb("general", job.organizationId);
    if (general) return { ...general, templateKey, vertical: templateKey };
  }
  return buildEmptyChecklist(templateKey);
}

export function resolveJobChecklist(job: Pick<Job, "projectType" | "scopeSummary" | "notes" | "projectConfig" | "checklist">): JobChecklist {
  const existing = job.checklist;
  if (existing?.items?.some((item) => String(item.label ?? "").trim())) {
    return {
      ...existing,
      items: normalizeChecklistItems(existing.items),
      updatedAt: existing.updatedAt ?? new Date().toISOString(),
    };
  }
  return buildDefaultJobChecklist(job);
}

export async function resolveJobChecklistAsync(job: ChecklistTemplateScope & Pick<Job, "checklist">): Promise<JobChecklist> {
  const existing = job.checklist;
  if (existing?.items?.some((item) => String(item.label ?? "").trim())) {
    return resolveJobChecklist(job);
  }
  return loadDefaultJobChecklist(job);
}

export function normalizeChecklistItems(items: Array<Partial<JobChecklistItem>>): JobChecklistItem[] {
  const activeItems = items
    .map((item, originalIndex) => ({ item, originalIndex }))
    .filter(({ item }) => item.isActive !== false && String(item.label ?? "").trim())
    .sort((a, b) => {
      const phaseA = JOB_CHECKLIST_PHASES.indexOf(a.item.phaseKey as JobChecklistPhaseKey);
      const phaseB = JOB_CHECKLIST_PHASES.indexOf(b.item.phaseKey as JobChecklistPhaseKey);
      if (phaseA !== phaseB) return phaseA - phaseB;
      const sortA = Number.isFinite(Number(a.item.sortOrder)) ? Number(a.item.sortOrder) : Number.MAX_SAFE_INTEGER;
      const sortB = Number.isFinite(Number(b.item.sortOrder)) ? Number(b.item.sortOrder) : Number.MAX_SAFE_INTEGER;
      if (sortA !== sortB) return sortA - sortB;
      const itemIndexA = Number.isFinite(Number(a.item.itemIndex)) ? Number(a.item.itemIndex) : Number.MAX_SAFE_INTEGER;
      const itemIndexB = Number.isFinite(Number(b.item.itemIndex)) ? Number(b.item.itemIndex) : Number.MAX_SAFE_INTEGER;
      if (itemIndexA !== itemIndexB) return itemIndexA - itemIndexB;
      return a.originalIndex - b.originalIndex;
    });

  const nextIndexByPhase = new Map<JobChecklistPhaseKey, number>();

  return activeItems.map(({ item }) => {
    const phaseKey = JOB_CHECKLIST_PHASES.includes(item.phaseKey as JobChecklistPhaseKey)
      ? item.phaseKey as JobChecklistPhaseKey
      : "install";
    const phaseIndex = nextIndexByPhase.get(phaseKey) ?? 0;
    nextIndexByPhase.set(phaseKey, phaseIndex + 1);
    return {
      phaseKey,
      phaseLabel: String(item.phaseLabel ?? "").trim() || resolvePhaseLabel(phaseKey),
      itemIndex: phaseIndex,
      label: String(item.label ?? "").trim(),
      proofLabel: String(item.proofLabel ?? "").trim() || null,
      requiresProof: Boolean(item.requiresProof),
      sortOrder: phaseIndex,
      isActive: true,
      templateId: item.templateId ?? null,
      templateItemId: item.templateItemId ?? null,
    };
  });
}

function sortChecklistItems(a: JobChecklistItem, b: JobChecklistItem): number {
  const phaseA = JOB_CHECKLIST_PHASES.indexOf(a.phaseKey);
  const phaseB = JOB_CHECKLIST_PHASES.indexOf(b.phaseKey);
  if (phaseA !== phaseB) return phaseA - phaseB;
  const sortA = Number.isFinite(Number(a.sortOrder)) ? Number(a.sortOrder) : Number.MAX_SAFE_INTEGER;
  const sortB = Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : Number.MAX_SAFE_INTEGER;
  if (sortA !== sortB) return sortA - sortB;
  return a.itemIndex - b.itemIndex;
}

function shouldPreferExpandedDefault(persisted: JobChecklistItem[], expandedDefault: JobChecklist): boolean {
  if (persisted.length === 0 || expandedDefault.items.length <= persisted.length) return false;
  const phaseCount = new Set(persisted.map((item) => item.phaseKey)).size;
  if (persisted.length > phaseCount + 1) return false;

  const defaultLabels = new Set(expandedDefault.items.map((item) => normalizeText(item.label)));
  return persisted.every((item) => defaultLabels.has(normalizeText(item.label)));
}

function inferTemplateKeyFromChecklistItems(items: JobChecklistItem[]): ChecklistTemplateKey {
  return inferChecklistTemplateKey({
    scopeSummary: items
      .flatMap((item) => [item.label, item.proofLabel ?? ""])
      .filter(Boolean)
      .join(" "),
  });
}

function looksLikeCompactTemplateRows(items: JobChecklistItem[]): boolean {
  const phaseCount = new Set(items.map((item) => item.phaseKey)).size;
  return items.length > 0 && items.length <= phaseCount + 1;
}

function nullableText(value: unknown): string | null {
  const text = String(value ?? "").trim();
  return text || null;
}

function rowNeedsChecklistPatch(
  row: ExistingChecklistRow,
  patch: Record<string, unknown>,
  item: JobChecklistItem,
): boolean {
  return (
    String(row.phase_key ?? "") !== item.phaseKey ||
    Number(row.item_index ?? -1) !== item.itemIndex ||
    nullableText(row.phase_label) !== nullableText(patch.phase_label) ||
    nullableText(row.label) !== nullableText(patch.label) ||
    nullableText(row.proof_label) !== nullableText(patch.proof_label) ||
    Boolean(row.requires_proof) !== Boolean(patch.requires_proof) ||
    Number(row.sort_order ?? -1) !== Number(patch.sort_order ?? -1) ||
    row.is_custom !== true ||
    row.is_active !== true ||
    nullableText(row.template_id) !== nullableText(patch.template_id) ||
    nullableText(row.template_item_id) !== nullableText(patch.template_item_id)
  );
}

export async function loadPersistedJobChecklist(
  job: Pick<Job, "id" | "organizationId" | "projectType" | "scopeSummary" | "notes" | "projectConfig" | "checklist">,
): Promise<{ data: JobChecklist | null; error: unknown | null }> {
  const jobId = String(job.id ?? "").trim();
  if (!jobId) return { data: null, error: null };

  const { data, error } = await supabase
    .from("job_checklist_items")
    .select("phase_key, phase_label, item_index, label, proof_label, requires_proof, sort_order, is_active, is_custom, template_id, template_item_id")
    .eq("job_id", jobId)
    .in("phase_key", JOB_CHECKLIST_PHASES);

  if (error) return { data: null, error };

  const rows = (data ?? []) as Array<Record<string, unknown>>;
  const templateId = String(rows.find((row) => String(row.template_id ?? "").trim())?.template_id ?? "").trim();
  let templatePhaseLabels: Partial<Record<JobChecklistPhaseKey, string>> = {};
  if (templateId) {
    const { data: phaseRows, error: phaseError } = await supabase
      .from("job_checklist_template_phases")
      .select("phase_key, label")
      .eq("template_id", templateId);
    if (!phaseError) {
      templatePhaseLabels = ((phaseRows ?? []) as ChecklistTemplatePhaseRow[]).reduce<Partial<Record<JobChecklistPhaseKey, string>>>((acc, row) => {
        const phase = String(row.phase_key ?? "");
        if (JOB_CHECKLIST_PHASES.includes(phase as JobChecklistPhaseKey)) {
          acc[phase as JobChecklistPhaseKey] = String(row.label ?? "").trim();
        }
        return acc;
      }, {});
    }
  }

  const items = rows
    .filter((row) => row.is_active !== false && row.is_custom === true && String(row.label ?? "").trim())
    .map((row): JobChecklistItem => {
      const phase = String(row.phase_key ?? "");
      const phaseKey = JOB_CHECKLIST_PHASES.includes(phase as JobChecklistPhaseKey)
        ? phase as JobChecklistPhaseKey
        : "install";
      return {
        phaseKey,
        phaseLabel: String(row.phase_label ?? "").trim() || resolvePhaseLabel(phaseKey, templatePhaseLabels),
        itemIndex: Number.isFinite(Number(row.item_index)) ? Number(row.item_index) : 0,
        label: String(row.label ?? "").trim(),
        proofLabel: String(row.proof_label ?? "").trim() || null,
        requiresProof: Boolean(row.requires_proof),
        sortOrder: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : undefined,
        isActive: true,
        templateId: String(row.template_id ?? "").trim() || null,
        templateItemId: String(row.template_item_id ?? "").trim() || null,
      };
    })
    .sort(sortChecklistItems);

  const expectedTemplateKey = inferChecklistTemplateKey(job);
  const expandedDefault = await loadDefaultJobChecklist(job);
  if (items.length === 0) return { data: expandedDefault.items.length ? expandedDefault : null, error: null };

  const linkedTemplate = await loadChecklistTemplateById(templateId);
  if (linkedTemplate && shouldPreferExpandedDefault(items, linkedTemplate)) {
    if (linkedTemplate.templateKey === expectedTemplateKey || expectedTemplateKey === "general") {
      return { data: linkedTemplate, error: null };
    }
    if (expandedDefault.items.length > 0) {
      return { data: expandedDefault, error: null };
    }
  }
  if (shouldPreferExpandedDefault(items, expandedDefault)) {
    return { data: expandedDefault, error: null };
  }
  const rowInferredTemplateKey = inferTemplateKeyFromChecklistItems(items);
  const rowInferredDefault = await loadChecklistTemplateFromDb(rowInferredTemplateKey, job.organizationId);
  if (
    expandedDefault.items.length > 0 &&
    rowInferredTemplateKey !== expectedTemplateKey &&
    expectedTemplateKey !== "general" &&
    rowInferredTemplateKey !== "general" &&
    looksLikeCompactTemplateRows(items)
  ) {
    return { data: expandedDefault, error: null };
  }
  if (rowInferredDefault && shouldPreferExpandedDefault(items, rowInferredDefault)) {
    return { data: rowInferredDefault, error: null };
  }

  return {
    data: {
      templateKey: expectedTemplateKey,
      vertical: expandedDefault.vertical || expectedTemplateKey,
      items,
      updatedAt: expandedDefault.updatedAt,
    },
    error: null,
  };
}

function isDispatched(job: Pick<Job, "currentStage" | "dispatchStatus" | "installStatus">): boolean {
  return Boolean(
    job.dispatchStatus === "Dispatched" ||
    job.installStatus === "En Route" ||
    job.installStatus === "On Site" ||
    job.installStatus === "In Progress" ||
    job.installStatus === "Install Complete" ||
    ["Assigned and Dispatched", "In Progress", "QA Review", "Completed"].includes(String(job.currentStage ?? ""))
  );
}

export async function syncJobChecklistItems(job: Pick<Job, "id" | "organizationId" | "projectType" | "scopeSummary" | "notes" | "projectConfig" | "checklist" | "currentStage" | "dispatchStatus" | "installStatus">): Promise<{ error: unknown | null }> {
  const jobId = String(job.id ?? "").trim();
  const organizationId = String(job.organizationId ?? "").trim();
  if (!jobId || !organizationId) return { error: null };

  const checklist = await resolveJobChecklistAsync(job);
  const items = normalizeChecklistItems(checklist.items);

  const keepKeys = new Set(items.map((item) => `${item.phaseKey}:${item.itemIndex}`));
  const { data: existingRows, error: existingError } = await supabase
    .from("job_checklist_items")
    .select("id, phase_key, phase_label, item_index, label, proof_label, requires_proof, sort_order, is_custom, is_active, template_id, template_item_id")
    .eq("job_id", jobId)
    .in("phase_key", JOB_CHECKLIST_PHASES);

  if (existingError) return { error: existingError };
  const rows = (existingRows ?? []) as ExistingChecklistRow[];

  if (items.length === 0) {
    const customRowIds = rows
      .filter((row) => row.is_custom === true && row.is_active !== false)
      .map((row) => String(row.id ?? ""))
      .filter(Boolean);

    if (customRowIds.length > 0) {
      const query = supabase.from("job_checklist_items");
      const removal = isDispatched(job)
        ? await query.update({ is_active: false }).in("id", customRowIds)
        : await query.delete().in("id", customRowIds);
      if (removal.error) return { error: removal.error };
    }

    return { error: null };
  }

  const existingByKey = new Map(
    rows.map((row) => [
      `${String(row.phase_key ?? "")}:${Number(row.item_index ?? -1)}`,
      row,
    ]),
  );
  const existingByPhaseAndLabel = new Map(
    rows
      .filter((row) => String(row.label ?? "").trim())
      .map((row) => [
        `${String(row.phase_key ?? "")}:${normalizeText(row.label)}`,
        row,
      ]),
  );

  const inserts: Record<string, unknown>[] = [];
  const updates: Array<{ id: string; patch: Record<string, unknown> }> = [];
  const keptExistingIds = new Set<string>();
  for (const item of items) {
    const key = `${item.phaseKey}:${item.itemIndex}`;
    const patch = {
      organization_id: organizationId,
      phase_label: item.phaseLabel ?? resolvePhaseLabel(item.phaseKey),
      label: item.label,
      proof_label: item.proofLabel ?? null,
      requires_proof: item.requiresProof,
      sort_order: item.sortOrder ?? item.itemIndex,
      is_custom: true,
      is_active: true,
      template_id: item.templateId ?? null,
      template_item_id: item.templateItemId ?? null,
    };
    const existingRow = existingByKey.get(key) ?? existingByPhaseAndLabel.get(`${item.phaseKey}:${normalizeText(item.label)}`);
    const existingId = String(existingRow?.id ?? "");
    if (existingRow && existingId) {
      keptExistingIds.add(existingId);
      if (rowNeedsChecklistPatch(existingRow, patch, item)) {
        updates.push({
          id: existingId,
          patch: { ...patch, phase_key: item.phaseKey, item_index: item.itemIndex },
        });
      }
    } else {
      inserts.push({
        job_id: jobId,
        phase_key: item.phaseKey,
        item_index: item.itemIndex,
        checked: false,
        ...patch,
      });
    }
  }

  for (const update of updates) {
    const { error } = await supabase
      .from("job_checklist_items")
      .update(update.patch)
      .eq("id", update.id);
    if (error) return { error };
  }

  if (inserts.length > 0) {
    const { error } = await supabase.from("job_checklist_items").insert(inserts);
    if (error) return { error };
  }

  const removedIds = rows
    .filter((row) => {
      const rowId = String(row.id ?? "");
      if (keptExistingIds.has(rowId)) return false;
      if (row.is_custom !== true || row.is_active === false) return false;
      return !keepKeys.has(`${String(row.phase_key ?? "")}:${Number(row.item_index ?? -1)}`);
    })
    .map((row) => String(row.id ?? ""))
    .filter(Boolean);

  if (removedIds.length > 0) {
    const query = supabase.from("job_checklist_items");
    const removal = isDispatched(job)
      ? await query.update({ is_active: false }).in("id", removedIds)
      : await query.delete().in("id", removedIds);
    if (removal.error) return { error: removal.error };
  }

  return { error: null };
}
